<main
  class="content sprays-content"
  data-test="fms-diagnose-sprays"
  [attr.aria-label]="'fmsDiagnose.tabs.sprays.title' | translate"
>
  @if (spraysState$ | async; as state) {
    <ng-template #refreshButton>
      <button
        mat-stroked-button
        class="sync-from-fms-button"
        type="button"
        [disabled]="state.kind === 'loading' || state.kind === 'syncing'"
        [matTooltip]="'fmsDiagnose.tabs.sprays.refreshTooltip' | translate"
        (click)="requestRetry()"
        data-test="fms-diagnose-refresh-sprays"
      >
        <span class="sync-from-fms-button__content">
          @if (state.kind === 'syncing') {
            <mat-spinner diameter="18" strokeWidth="3"></mat-spinner>
          } @else {
            <img
              src="assets/icons/sync-from-fms.svg"
              alt=""
              aria-hidden="true"
              class="sync-from-fms-button__icon"
              height="18"
              width="22"
            />
          }
          <span>{{ 'fmsDiagnose.tabs.sprays.refresh' | translate }}</span>
        </span>
      </button>
    </ng-template>

    @if (state.kind === 'loading') {
      <div class="sprays-actions">
        <ng-container [ngTemplateOutlet]="refreshButton"></ng-container>
      </div>
      <output class="sprays-state" aria-live="polite">
        <mat-spinner
          diameter="40"
          [attr.aria-label]="'fmsDiagnose.shared.loading' | translate"
        ></mat-spinner>
      </output>
    } @else if (state.kind === 'syncing') {
      <div class="sprays-actions">
        <ng-container [ngTemplateOutlet]="refreshButton"></ng-container>
      </div>
      <output class="sprays-state" aria-live="polite">
        <mat-spinner
          diameter="40"
          [attr.aria-label]="'fmsDiagnose.shared.loading' | translate"
        ></mat-spinner>
        <span>{{ 'fmsDiagnose.tabs.sprays.syncing' | translate }}</span>
      </output>
    } @else if (state.kind === 'error') {
      <div class="sprays-actions">
        <ng-container [ngTemplateOutlet]="refreshButton"></ng-container>
      </div>
      <output class="sprays-state" role="alert">
        <mat-icon aria-hidden="true">error</mat-icon>
        <span>{{ 'fmsDiagnose.tabs.sprays.error' | translate }}</span>
        <button mat-stroked-button type="button" (click)="requestRetry()">
          {{ 'fmsDiagnose.shared.retry' | translate }}
        </button>
      </output>
    } @else if (state.kind === 'loaded') {
      @let cropFields = visibleCropFields(state.data);
      @let summary = spraySummary(state.data);
      @if (cropFields.length === 0) {
        <div class="sprays-actions">
          <ng-container [ngTemplateOutlet]="refreshButton"></ng-container>
        </div>
        <output class="sprays-state" data-test="fms-diagnose-sprays-empty">
          <mat-icon aria-hidden="true">format_list_bulleted</mat-icon>
          <span>{{ 'fmsDiagnose.tabs.sprays.empty' | translate }}</span>
        </output>
      } @else {
        <section
          class="sprays-summary sprays-summary--success"
          aria-labelledby="fms-diagnose-sprays-summary-title"
        >
          <div class="sprays-summary__header">
            <div class="sprays-summary__intro">
              <mat-icon aria-hidden="true">check</mat-icon>
              <div>
                <h3 id="fms-diagnose-sprays-summary-title">
                  {{ summary.sprayCount }}
                  {{ 'fmsDiagnose.tabs.sprays.summary.sprays' | translate }}
                  {{ 'fmsDiagnose.tabs.sprays.summary.across' | translate }}
                  {{ summary.cropFieldCount }}
                  {{ 'fmsDiagnose.tabs.sprays.summary.cropFields' | translate }}
                </h3>
                <p>{{ 'fmsDiagnose.tabs.sprays.summary.description' | translate }}</p>
              </div>
            </div>
            <ng-container [ngTemplateOutlet]="refreshButton"></ng-container>
          </div>
          <div class="sprays-summary-metrics">
            <div class="sprays-summary-metric" data-test="fms-diagnose-spray-summary-virus">
              <span class="spray-badge spray-badge--virus">
                <strong>{{ summary.virusCount }}</strong>
                <span>{{ 'fmsDiagnose.tabs.sprays.categories.virus' | translate }}</span>
              </span>
            </div>
            <div class="sprays-summary-metric" data-test="fms-diagnose-spray-summary-phytophthora">
              <span class="spray-badge spray-badge--phytophthora">
                <strong>{{ summary.phytophthoraCount }}</strong>
                <span>{{ 'fmsDiagnose.tabs.sprays.categories.phytophthora' | translate }}</span>
              </span>
            </div>
            <div class="sprays-summary-metric" data-test="fms-diagnose-spray-summary-downy-mildew">
              <span class="spray-badge spray-badge--downyMildew">
                <strong>{{ summary.downyMildewCount }}</strong>
                <span>{{ 'fmsDiagnose.tabs.sprays.categories.downyMildew' | translate }}</span>
              </span>
            </div>
            <div class="sprays-summary-metric" data-test="fms-diagnose-spray-summary-not-linked">
              <span class="spray-badge spray-badge--notLinked">
                <strong>{{ summary.notLinkedCount }}</strong>
                <span>{{ 'fmsDiagnose.tabs.sprays.notLinked' | translate }}</span>
              </span>
            </div>
          </div>
        </section>

        <section class="spray-card-grid">
          @for (cropField of cropFields; track cropField.id) {
            <article class="spray-card" data-test="fms-diagnose-spray-crop-field">
              <header class="spray-card__header">
                <div class="spray-card__header-main">
                  <div class="spray-card__title">
                    <h3>{{ cropField.name }}</h3>
                    <span class="spray-card__area"
                      >{{ cropField.areaInHectares | number: '1.2-2' }} ha</span
                    >
                  </div>
                  <p>{{ cropName(cropField) }}</p>
                </div>
                <span class="spray-count-badge">
                  <strong>{{ cropField.sprayApplications.length }}</strong>
                  <span>{{ 'fmsDiagnose.tabs.sprays.sprayCount' | translate }}</span>
                </span>
              </header>
              <div class="spray-list" data-test="fms-diagnose-spray-list">
                @for (spray of cropField.sprayApplications; track spray.id) {
                  <div class="spray-row" data-test="fms-diagnose-spray-row">
                    <time>{{ spray.applicationDate | date: 'dd-MM-yyyy' }}</time>
                    <div class="spray-products">
                      @for (
                        cropAid of spray.cropAids;
                        track cropAid.cropAidCtgbNumber + cropAid.name
                      ) {
                        <div class="spray-product">
                          <strong>{{ cropAid.name }}</strong>
                          <span
                            >{{ cropAid.dosage | number: '1.0-3' }} {{ cropAid.dosageUnit }}</span
                          >
                        </div>
                      }
                    </div>
                    <div class="spray-row__chips">
                      @for (category of categories(spray); track category) {
                        <span [class]="'spray-badge spray-badge--' + category">
                          {{ 'fmsDiagnose.tabs.sprays.categories.' + category | translate }}
                        </span>
                      }
                    </div>
                  </div>
                }
              </div>
            </article>
          }
        </section>
      }
    }
  }
</main>
